import { z, ZodRawShape } from 'zod';
import { requiredPatientFieldPath } from './constants';
import { TRequiredUserField } from '@/types';

/**
 * Builds a Zod schema for patient-related required fields such as insurance, insurance policy number, and tax ID.
 * Each field can be marked as required or optional depending on the provided `requiredFields` array.
 * Validation rules for each field include maximum length and allowed character patterns.
 * @param t - Translation function used to generate localized error messages.
 * @param requiredFields - Array of field keys that should be marked as required. Defaults to an empty array.
 * @returns A Zod raw shape object with the specified required/optional fields and validation rules.
 */
export const buildPatientRequiredFieldsSchema = (
  t: (key: string, data?: Record<string, unknown>) => string,
  requiredFields: TRequiredUserField[],
): ZodRawShape => {
  const makeRequired = (isRequired: boolean, schema: z.ZodString) =>
    isRequired
      ? schema.min(1, { message: t('zod.field-required') })
      : schema.optional();

  const insurance = z
    .string()
    .max(50, { message: t('zod.max-number-symbols-allowed', { count: 50 }) })
    .regex(/^[a-zA-Z\u00C0-\u017F0-9\s]*$/, {
      message: t('zod.field-must-contain-only-numbers-and-letters'),
    });

  const insurancePolicyNumber = z
    .string()
    .max(16, { message: t('zod.max-number-symbols-allowed', { count: 16 }) })
    .regex(/^[a-zA-Z0-9-/]*$/, {
      message: t('zod.field-must-contain-only-numbers-letters-and-symbols'),
    });

  const taxId = z
    .string()
    .max(16, { message: t('zod.max-number-symbols-allowed', { count: 16 }) })
    .regex(/^[a-zA-Z0-9]*$/, {
      message: t('zod.field-must-contain-only-numbers-and-letters'),
    });

  return {
    insurance: makeRequired(
      requiredFields.includes(requiredPatientFieldPath.insurance),
      insurance,
    ),
    insurancePolicyNumber: makeRequired(
      requiredFields.includes(requiredPatientFieldPath.policyNumber),
      insurancePolicyNumber,
    ),
    taxId: makeRequired(
      requiredFields.includes(requiredPatientFieldPath.taxId),
      taxId,
    ),
  };
};
